import React, { Fragment, useState } from 'react';
import camelCase from 'lodash/camelCase';
import { useTranslation } from 'react-i18next';

import {
  Button,
  ButtonsWrapper,
  DatePickerInput,
  DeleteModal,
  Form,
  Icon,
  ICONS,
  NumberInput,
  PLATFORM_THEME_COLORS,
  SelectInput,
} from '@thinknet-gmbh/platform-common-ui';

import { EmployeeSalaryTypesEnum } from '@thinknet-gmbh/platform-common';

import { useDeleteWagesAndSalaryById } from 'src/hooks/wages-and-salary/delete-wages-and-salary-by-id.hook';

import {
  DeleteContainer,
  FormFieldsContainer,
  FormListContainer,
} from './wages-and-salary-form.styles';

import { IWageAndSalaryItem } from '../wages-and-salary.interfaces';

const WagesAndSalaryForm = ({
  onCancel,
  initialValues,
  onFinish,
}: {
  onFinish: (values: { salaries: IWageAndSalaryItem[] }) => void;
  onCancel: () => void;
  initialValues: {
    salaries: IWageAndSalaryItem[];
  };
}) => {
  const { t, i18n } = useTranslation(['common', 'employee']);
  const [salaryToDelete, setSalaryToDelete] = useState<{
    salary: IWageAndSalaryItem;
    field: number;
  } | null>(null);

  const [form] = Form.useForm();

  const deleteWagesAndSalaryById = useDeleteWagesAndSalaryById();

  const deleteSalaryHandler = (name: number, remove: (name: number) => void) => {
    const salary = form.getFieldValue(['salaries', name]);

    if (salary?._id) {
      setSalaryToDelete({ salary, field: name });
    } else {
      remove(name);
    }
  };

  const onSubmitDeleteModal = (remove: (name: number) => void) => {
    deleteWagesAndSalaryById({
      variables: { id: salaryToDelete?.salary?._id },
      onCompleted: data => {
        if (data) {
          if (salaryToDelete?.field) {
            remove(salaryToDelete.field);
          }
          setSalaryToDelete(null);
        }
      },
    });
  };

  return (
    <Form layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <Form.List name="salaries">
        {(fields, { add, remove }) => (
          <Fragment>
            <DeleteModal
              title={t('employee:wagesAndSalary.deleteModal.title')}
              subTitle={t('employee:wagesAndSalary.deleteModal.subtitle')}
              onSubmit={() => onSubmitDeleteModal(remove)}
              submitBtnText={t('common:buttons.delete')}
              translate={t}
              onClose={() => setSalaryToDelete(null)}
              isOpened={!!salaryToDelete}
            />
            <FormListContainer>
              {fields.map(field => {
                return (
                  <FormFieldsContainer key={field.key}>
                    <Form.Item
                      name={[field.name, 'salaryType']}
                      label={t('employee:labels.salaryType')}
                      rules={[
                        {
                          required: true,
                          message: t('employee:validationError.salaryTypeRequired'),
                        },
                      ]}
                    >
                      <SelectInput placeholder={t('employee:workingConditions.labels.salaryWages')}>
                        {Object.keys(EmployeeSalaryTypesEnum).map(salary => (
                          <SelectInput.Option key={salary} value={salary}>
                            {t(`employee:workingConditions.labels.${camelCase(salary)}`)}
                          </SelectInput.Option>
                        ))}
                      </SelectInput>
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'startDate']}
                      label={t('employee:labels.salaryStartDate')}
                      rules={[
                        {
                          required: true,
                          message: t('employee:validationError.salaryStartDateRequired'),
                        },
                      ]}
                    >
                      <DatePickerInput lang={i18n.language} format="DD.MM.YYYY" />
                    </Form.Item>
                    <DeleteContainer>
                      <Form.Item
                        name={[field.name, 'amount']}
                        label={t('employee:labels.salaryAmount')}
                        rules={[
                          {
                            required: true,
                            message: t('employee:validationError.salaryAmountRequired'),
                          },
                        ]}
                      >
                        <NumberInput prefix="€" placeholder="€1, 700" />
                      </Form.Item>
                      {fields.length > 1 && (
                        <Icon
                          icon={ICONS.trash}
                          className={'remove-salary-icon'}
                          rightMargin={false}
                          color={PLATFORM_THEME_COLORS.tone.red}
                          size={22}
                          onClick={() => {
                            deleteSalaryHandler(field.name, remove);
                          }}
                        />
                      )}
                    </DeleteContainer>
                  </FormFieldsContainer>
                );
              })}
              <Button
                type="text"
                icon={ICONS.plus}
                iconColor={PLATFORM_THEME_COLORS.primary.blue}
                onClick={() => add()}
              >
                {t('employee:workingConditions.buttons.addNewSalary')}
              </Button>
            </FormListContainer>
          </Fragment>
        )}
      </Form.List>
      <ButtonsWrapper>
        <Button type="primary" htmlType="submit">
          {t('common:buttons.save')}
        </Button>
        <Button onClick={onCancel}>{t('common:buttons.cancel')}</Button>
      </ButtonsWrapper>
    </Form>
  );
};

export default WagesAndSalaryForm;
